import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, UNAVAIL_LABEL } from "../lib/api";
import { can, isSystemAdmin } from "../lib/permissions";

const isAdmin = (user) => isSystemAdmin(user) || can(user, "unavailability.manage");
import { Sheet, Button } from "./ui";
import { Icon } from "./icons";

const pad = (n) => String(n).padStart(2, "0");
export const dayIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const pt = (iso) => iso.split("-").reverse().join("/");

/** Unavailability periods, shared with the Mobile app (/me/unavailability; admins see everyone's). */
export function useUnavailability(user, y, m) {
  const all = can(user, "unavailability.view_all");
  const start = dayIso(y, m, 1);
  const end = dayIso(y, m, new Date(y, m + 1, 0).getDate());
  const q = useQuery({ queryKey: ["unavailability", all, y, m], queryFn: () => api(all ? `/admin/unavailability?start=${start}&end=${end}` : "/me/unavailability") });
  return { all, list: q.data || [], on: (iso) => (q.data || []).filter((u) => u.start <= iso && iso <= u.end) };
}

/** Admin deletion of anyone's period (contract U1); members delete their own via /me. */
const deleteUnavail = async (u, mine) => {
  if (mine) return api(`/me/unavailability/${u.id}`, { method: "DELETE" });
  try {
    return await api(`/admin/unavailability/${u.id}`, { method: "DELETE" });
  } catch (err) {
    if ([404, 405].includes(err.status)) throw new Error("O servidor do coro ainda não permite à direcção eliminar indisponibilidades de outros membros (pendente no backend Mobile).");
    throw err;
  }
};

export function DayUnavailable({ items, all, user }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  return (
    <div className="card" style={{ padding: 12, borderColor: "var(--warning)", display: "flex", flexDirection: "column", gap: 6 }} data-testid="calendar-day-unavailable">
      <div className="t-kicker" style={{ color: "var(--warning)" }}>{all ? `INDISPONÍVEIS NESTE DIA · ${items.length}` : "INDISPONÍVEL NESTE DIA"}</div>
      {items.map((u) => (
        <button key={u.id} onClick={() => setOpen(u)} style={{ fontSize: 14, display: "flex", gap: 8, alignItems: "center", background: "none", border: "none", color: "inherit", padding: "6px 0", cursor: "pointer", textAlign: "left" }} data-testid={`calendar-unavail-${u.id}`}>
          <Icon name="warning" size={14} color="var(--warning)" />
          <span style={{ flex: 1 }}>{all && u.user ? <b>{u.user.name}</b> : null}{all && u.user ? " · " : ""}{UNAVAIL_LABEL[u.kind] || u.kind}</span>
          <Icon name="chevron-forward" size={14} color="var(--muted)" />
        </button>
      ))}
      {open ? <UnavailDetail u={open} user={user} all={all} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function UnavailDetail({ u, user, all, onClose }) {
  const qc = useQueryClient();
  const mine = (u.user_id || u.user?.id) === user?.id;
  const canDelete = mine || isAdmin(user);
  const [err, setErr] = useState("");
  const del = useMutation({
    mutationFn: () => deleteUnavail(u, mine),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["unavailability"] }); onClose(); },
    onError: (e) => setErr(e.message),
  });
  return (
    <Sheet title="Indisponibilidade" onClose={onClose} testid="unavail-detail">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {all && u.user ? <div><div className="t-hint">Membro</div><div style={{ fontFamily: "var(--f-medium)" }} data-testid="unavail-detail-name">{u.user.name}{u.user.voice ? ` · ${u.user.voice}` : ""}</div></div> : null}
        <div><div className="t-hint">Motivo</div><div>{UNAVAIL_LABEL[u.kind] || u.kind}{u.reason ? ` — ${u.reason}` : ""}</div></div>
        <div><div className="t-hint">Período</div><div data-testid="unavail-detail-dates">{u.start === u.end ? pt(u.start) : `${pt(u.start)} a ${pt(u.end)}`}</div></div>
        {err ? <div style={{ color: "var(--error)", fontSize: 13 }} data-testid="unavail-detail-error">{err}</div> : null}
        {canDelete ? (
          <Button onClick={() => window.confirm(mine ? "Remover a sua indisponibilidade?" : `Eliminar a indisponibilidade de ${u.user?.name || "este membro"}?`) && del.mutate()} disabled={del.isPending} style={{ background: "var(--error)" }} data-testid="unavail-detail-delete">
            {mine ? "REMOVER" : "ELIMINAR (DIRECÇÃO)"}
          </Button>
        ) : null}
      </div>
    </Sheet>
  );
}

export function MyUnavailability({ user, initialDay }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const mine = useQuery({ queryKey: ["unavailability", "me"], queryFn: () => api("/me/unavailability") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["unavailability"] });
  const del = useMutation({ mutationFn: (id) => api(`/me/unavailability/${id}`, { method: "DELETE" }), onSuccess: refresh, onError: (e) => window.alert(e.message) });
  if (user?.is_singer === false) return null;
  const today = new Date().toISOString().slice(0, 10);
  const list = (mine.data || []).filter((u) => u.end >= today);
  return (
    <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 8 }} data-testid="my-unavailability">
      <div className="row-between">
        <span className="t-section">As minhas indisponibilidades</span>
        <Button className="auto sm ghost" icon="add" onClick={() => setOpen(true)} data-testid="add-unavailability-button">MARCAR</Button>
      </div>
      {list.length ? list.map((u) => (
        <div key={u.id} className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }} data-testid={`my-unavail-${u.id}`}>
          <Icon name="warning" size={16} color="var(--warning)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{UNAVAIL_LABEL[u.kind] || u.kind}{u.reason ? ` — ${u.reason}` : ""}</div>
            <div className="t-hint">{u.start === u.end ? pt(u.start) : `${pt(u.start)} a ${pt(u.end)}`}</div>
          </div>
          <button className="icon-btn" aria-label="Remover indisponibilidade" onClick={() => window.confirm("Remover esta indisponibilidade?") && del.mutate(u.id)} data-testid={`my-unavail-delete-${u.id}`}><Icon name="trash" size={16} color="var(--muted)" /></button>
        </div>
      )) : <div className="t-hint" data-testid="my-unavail-empty">Sem indisponibilidades marcadas. Marque férias ou outros períodos para a direcção saber quem está disponível.</div>}
      {open ? <UnavailSheet initialDay={initialDay || today} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); refresh(); }} /> : null}
    </div>
  );
}

function UnavailSheet({ initialDay, onClose, onSaved }) {
  const [kind, setKind] = useState("ferias");
  const [start, setStart] = useState(initialDay);
  const [end, setEnd] = useState(initialDay);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const add = useMutation({ mutationFn: (v) => api("/me/unavailability", { method: "POST", body: JSON.stringify(v) }), onSuccess: onSaved, onError: (e) => setErr(e.message) });
  const submit = () => {
    if (!start || !end) return setErr("Indique as datas.");
    if (end < start) return setErr("A data de fim deve ser igual ou posterior à de início.");
    if (kind === "outros" && !reason.trim()) return setErr("Descreva o motivo da indisponibilidade.");
    setErr("");
    add.mutate({ kind, start, end, reason: reason.trim() });
  };
  return (
    <Sheet title="Marcar indisponibilidade" onClose={onClose} testid="unavail-modal">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="t-hint">A direcção vê um alerta de indisponibilidade ao convocar para compromissos nestas datas.</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.keys(UNAVAIL_LABEL).map((k) => <button key={k} className={`chip ${kind === k ? "active" : ""}`} onClick={() => setKind(k)} data-testid={`unavail-kind-${k}`}>{UNAVAIL_LABEL[k].toUpperCase()}</button>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <label className="t-hint">INÍCIO<input type="date" className="input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="unavail-start-input" /></label>
          <label className="t-hint">FIM<input type="date" className="input" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="unavail-end-input" /></label>
        </div>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === "outros" ? "Motivo (obrigatório)" : "Nota (opcional)"} maxLength={140} data-testid="unavail-reason-input" />
        {err ? <div style={{ color: "var(--error)", fontSize: 13 }} data-testid="unavail-error">{err}</div> : null}
        <Button onClick={submit} disabled={add.isPending} data-testid="unavail-submit-button">GUARDAR</Button>
      </div>
    </Sheet>
  );
}
